import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, timeout, switchMap, filter, take } from 'rxjs/operators';
import { LogStatus, LogError } from '@memberjunction/core';
import { UserService } from '../services/user.service';
import { ResourcesEntity } from 'mj_generatedentities';
import { SharedData } from "../utils/shared-data";
import { ImpersonationService } from '../services/impersonation.service';
import { UserAuthService } from '../services/user-auth.service';
import { SYSTEM_ROLES } from '../utils/constants';
import { ActiveUserService } from '../services/active-user.service';

@Injectable({
    providedIn: 'root'
})
export class RoleGuard {
    private userPermissions: Map<string, Set<string>> = new Map();
    private resourceMap: Map<string, ResourcesEntity> = new Map();
    private permissionMap: Map<string, string> = new Map();
    private permissionsLoaded: boolean = false;

    constructor(
        private router: Router,
        private userService: UserService,
        private sharedData: SharedData,
        private impersonationService: ImpersonationService,
        private userAuthService: UserAuthService,
        private activeUserService: ActiveUserService
    ) {
        // Register this guard with ActiveUserService for cache clearing
        this.activeUserService.setRoleGuardInstance(this);
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.userService.getUserComplete$.pipe(
            filter(complete => complete),
            take(1),
            switchMap(() => this.checkPermissions(state.url)),
            timeout(10000),
            map((result: boolean | string) => {
                if (result === true) {
                    return true;
                } else {
                    LogStatus(`User does not have permission to access ${state.url}`);
                    // If already trying to access no-access page, allow it to prevent infinite loop
                    if (state.url === '/no-access') {
                        return true;
                    }

                    // Check if it's a "No Access" role scenario
                    if (result === 'no-access-role') {
                        return this.router.createUrlTree(['/no-access'], { queryParams: { reason: 'no-access-role' } });
                    } else {
                        // Regular permission denial
                        return this.router.createUrlTree(['/no-access']);
                    }
                }
            }),
            catchError((error) => {
                LogError('Role guard error:', undefined, error);
                return of(this.router.createUrlTree(['/dashboard']));
            })
        );
    }

    private async checkPermissions(url: string): Promise<boolean | string> {
        try {
            // For impersonation support - check if we're impersonating
            const sessionStatus = await this.impersonationService.getSessionStatus().pipe(take(1)).toPromise();

            // Get user role - works for both regular and impersonated users
            let userRoleName: string | null = null;

            if (sessionStatus?.isImpersonating && sessionStatus.session) {
                // For impersonated users, get role from ExtendedUsers using User_ID
                const impersonatedUserId = sessionStatus.session.impersonatedUser.ID;
                const impersonatedEmail = sessionStatus.session.impersonatedUser.Email;

                LogStatus(`Getting role for impersonated user: ${impersonatedEmail}`);
                userRoleName = await this.userAuthService.getRoleByUserId(impersonatedUserId, impersonatedEmail);

                if (!userRoleName) {
                    LogError(`Failed to get role for impersonated user ${impersonatedEmail}`);
                    return false;
                }
            } else {
                // Get user role from API for regular users
                userRoleName = this.userAuthService.getUserRole();
            }

            if (!userRoleName) {
                // Check if auth info is available before fetching role
                const personId = this.userAuthService.getPersonId();
                const auraUniqueId = this.userAuthService.getAuraUniqueId();

                if (!personId || !auraUniqueId) {
                    // Auth info not yet available, deny access for now
                    LogStatus('Auth info not yet available, denying access temporarily');
                    return false;
                }

                // Fetch role from API if not cached
                try {
                    userRoleName = await this.userAuthService.fetchUserRole();
                } catch (error) {
                    LogError('Error fetching user role from API:', undefined, error);
                    return false;
                }
            }

            // Check if user has access based on role
            if (userRoleName === 'No Access') {
                LogStatus(`User has "No Access" role, denying access to ${url}`);
                // Return special indicator for "No Access" role
                return 'no-access-role';
            }

            // Map role name to RoleEntity and check resource permissions
            return await this.checkResourcePermissionsWithApiRole(url, userRoleName);

        } catch (error) {
            LogError('Error checking permissions:', undefined, error);
            return false;
        }
    }

    private async checkResourcePermissionsWithApiRole(url: string, userRoleName: string): Promise<boolean> {
        try {
            // Load permissions data if not already loaded
            if (!this.permissionsLoaded) {
                await this.loadUserPermissions();
            }

            // Ensure SharedData permission data is loaded before getting roles
            if (!this.sharedData.isPermissionDataLoaded()) {
                LogStatus('SharedData permission data not loaded yet, waiting...');
                // Wait for SharedData to complete loading
                await new Promise<void>((resolve) => {
                    const checkInterval = setInterval(() => {
                        if (this.sharedData.isPermissionDataLoaded()) {
                            clearInterval(checkInterval);
                            resolve();
                        }
                    }, 50); // Check every 50ms
                });
            }

            // Get the RoleEntity that matches the API role name
            const roles = this.sharedData.getRoles();
            const matchingRole = roles.find(role => role.Name === userRoleName);

            if (!matchingRole) {
                const availableRoles = roles.map(r => `"${r.Name}" (ID: ${r.ID})`).join(', ');
                LogError(`No RoleEntity found for API role name: "${userRoleName}". Available roles: [${availableRoles}]`);
                return false;
            }

            // Find the resource for this URL
            const normalizedUrl = this.normalizeUrl(url);
            const resource = this.findResourceByUrl(normalizedUrl);

            if (!resource) {
                // No resource defined for this URL, allow access
                LogStatus(`No resource defined for URL: ${normalizedUrl}, allowing access`);
                return true;
            }

            // Check if the role has any permissions for this resource
            const roleResourcePermissions = this.sharedData.getRoleResourcePermissions();
            const hasPermission = roleResourcePermissions.some(rrp =>
                rrp.RoleID === matchingRole.ID &&
                rrp.ResourceID === resource.ID &&
                rrp.IsActive
            );

            return hasPermission;

        } catch (error) {
            LogError('Error checking resource permissions with API role:', undefined, error);
            return false;
        }
    }

    private async checkLegacyPermissions(url: string): Promise<boolean> {
        try {
            // Check for active impersonation session and use appropriate user
            const sessionStatus = await this.impersonationService.getSessionStatus().pipe(take(1)).toPromise();

            let userInfo;
            if (sessionStatus?.isImpersonating) {
                // Get full user info with roles for impersonated user
                userInfo = await this.impersonationService.getImpersonatedUserWithRoles();
            } else {
                userInfo = this.userService.getUserInfo();
            }

            if (!userInfo) {
                return false;
            }

            // Get application roles only (exclude system roles)
            const userRoles = userInfo.UserRoles || [];
            const applicationRoles = userRoles.filter((roleEntity: any) =>
                !SYSTEM_ROLES.includes(roleEntity.Role)
            );

            if (applicationRoles.length === 0) {
                // No application roles, deny access
                return false;
            }

            if (!this.permissionsLoaded) {
                await this.loadUserPermissions();
            }

            const normalizedUrl = this.normalizeUrl(url);
            const resource = this.findResourceByUrl(normalizedUrl);

            if (!resource) {
                return true;
            }

            const permissions = this.userPermissions.get(resource.ID);

            if (!permissions || permissions.size === 0) {
                return false;
            }

            // Check for READ permission or any other permission that grants access
            const hasPermission = permissions.has('READ') || permissions.has('WRITE') || permissions.has('APPROVE') || permissions.has('DELETE');
            return hasPermission;
        } catch (error) {
            LogError('Error checking legacy permissions:', undefined, error);
            return false;
        }
    }

    private async loadUserPermissions(): Promise<void> {
        try {
            // Clear existing permissions first
            this.userPermissions.clear();
            
            const permissions = this.sharedData.getPermissions();
            const resources = this.sharedData.getResources();
            const rolePermissions = this.sharedData.getRoleResourcePermissions();

            // Create permission ID to code mapping
            permissions.forEach(permission => {
                this.permissionMap.set(permission.ID, permission.PermissionCode);
            });

            resources.forEach(resource => {
                if (resource.URL) {
                    this.resourceMap.set(resource.URL, resource);
                }
            });


            // Get current user and their roles
            const sessionStatus = await this.impersonationService.getSessionStatus().pipe(take(1)).toPromise();
            let userInfo;
            if (sessionStatus?.isImpersonating) {
                userInfo = await this.impersonationService.getImpersonatedUserWithRoles();
            } else {
                userInfo = this.userService.getUserInfo();
            }

            if (!userInfo || !userInfo.UserRoles) {
                this.permissionsLoaded = true;
                return;
            }

            // Get user's application role IDs (exclude system roles)
            const userRoleIds = userInfo.UserRoles
                .filter(ur => ur.Role && !SYSTEM_ROLES.includes(ur.Role))
                .map(ur => ur.RoleID);
            

            // Filter role permissions to only include the user's roles - EXACT match only
            const userRolePermissions = rolePermissions.filter(rrp => {
                const isActive = rrp.IsActive;
                
                // Only exact match - no fuzzy logic
                const roleMatches = userRoleIds.includes(rrp.RoleID);
                
                
                return isActive && roleMatches;
            });
            

            // Build user permissions map from filtered role permissions
            userRolePermissions.forEach(rrp => {
                if (!this.userPermissions.has(rrp.ResourceID)) {
                    this.userPermissions.set(rrp.ResourceID, new Set());
                }
                // Get actual permission code from permission map
                const permissionCode = this.permissionMap.get(rrp.PermissionID);
                if (permissionCode) {
                    this.userPermissions.get(rrp.ResourceID)?.add(permissionCode);
                }
            });


            this.permissionsLoaded = true;
        } catch (error) {
            LogError('Error loading permissions:', undefined, error);
            throw error;
        }
    }


    private normalizeUrl(url: string): string {
        url = url.split('?')[0].split('#')[0];
        
        if (url.startsWith('/')) {
            url = url.substring(1);
        }
        if (url.endsWith('/')) {
            url = url.substring(0, url.length - 1);
        }
        
        return url;
    }

    private findResourceByUrl(url: string): ResourcesEntity | undefined {
        return this.resourceMap.get(url);
    }

    public async getUserPermissions(): Promise<Map<string, Set<string>>> {
        if (!this.permissionsLoaded) {
            await this.loadUserPermissions();
        }
        return this.userPermissions;
    }

    public async canAccessResource(resourceUrl: string): Promise<boolean> {
        try {
            // Check for active impersonation session and use appropriate user
            const sessionStatus = await this.impersonationService.getSessionStatus().pipe(take(1)).toPromise();

            // Get user role - works for both regular and impersonated users
            let userRoleName: string | null = null;

            if (sessionStatus?.isImpersonating && sessionStatus.session) {
                // For impersonated users, get role from ExtendedUsers using User_ID
                const impersonatedUserId = sessionStatus.session.impersonatedUser.ID;
                const impersonatedEmail = sessionStatus.session.impersonatedUser.Email;

                LogStatus(`Getting role for impersonated user: ${impersonatedEmail}`);
                userRoleName = await this.userAuthService.getRoleByUserId(impersonatedUserId, impersonatedEmail);

                if (!userRoleName) {
                    LogError(`Failed to get role for impersonated user ${impersonatedEmail}`);
                    return false;
                }
            } else {
                // Get user role from API for regular users
                userRoleName = this.userAuthService.getUserRole();
            }

            if (!userRoleName) {
                // Check if auth info is available before fetching role
                const personId = this.userAuthService.getPersonId();
                const auraUniqueId = this.userAuthService.getAuraUniqueId();

                if (!personId || !auraUniqueId) {
                    // Auth info not yet available, deny access for now
                    LogStatus('Auth info not yet available, denying access temporarily');
                    return false;
                }

                // Fetch role from API if not cached
                try {
                    userRoleName = await this.userAuthService.fetchUserRole();
                } catch (error) {
                    LogError('Error fetching user role from API:', undefined, error);
                    return false;
                }
            }

            // Check if user has access based on role
            if (userRoleName === 'No Access') {
                return false;
            }

            // Use the same resource permission logic as the route guard
            return await this.checkResourcePermissionsWithApiRole(resourceUrl, userRoleName);
        } catch (error) {
            LogError('Error checking resource access:', undefined, error);
            return false;
        }
    }

    private async canAccessResourceLegacy(resourceUrl: string): Promise<boolean> {
        // Check for active impersonation session and use appropriate user
        const sessionStatus = await this.impersonationService.getSessionStatus().pipe(take(1)).toPromise();

        let userInfo;
        if (sessionStatus?.isImpersonating) {
            // Get full user info with roles for impersonated user
            userInfo = await this.impersonationService.getImpersonatedUserWithRoles();
        } else {
            userInfo = this.userService.getUserInfo();
        }

        if (!userInfo) {
            return false;
        }

        // Get application roles only (exclude system roles)
        const userRoles = userInfo.UserRoles || [];
        const applicationRoles = userRoles.filter((roleEntity: any) =>
            !SYSTEM_ROLES.includes(roleEntity.Role)
        );

        if (applicationRoles.length === 0) {
            return false;
        }

        if (!this.permissionsLoaded) {
            await this.loadUserPermissions();
        }

        const normalizedUrl = this.normalizeUrl(resourceUrl);
        const resource = this.findResourceByUrl(normalizedUrl);

        if (!resource) {
            return true;
        }

        const permissions = this.userPermissions.get(resource.ID);
        const hasAccess = permissions !== undefined && (permissions.has('READ') || permissions.has('WRITE') || permissions.has('APPROVE') || permissions.has('DELETE'));

        return hasAccess;
    }

    public clearPermissionsCache(): void {
        this.userPermissions.clear();
        this.resourceMap.clear();
        this.permissionMap.clear();
        this.permissionsLoaded = false;
    }

    /**
     * Should be called when user context changes (impersonation start/stop)
     * to ensure permissions are reloaded for the new user
     */
    public onUserContextChanged(): void {
        this.clearPermissionsCache();
        // Clear role cache to ensure fresh role lookups for the new user context
        this.userAuthService.clearRoleCache();
    }

    /**
     * Get the current user's role from the API (handles impersonation)
     */
    public async getUserRole(): Promise<string | null> {
        try {
            const sessionStatus = await this.impersonationService.getSessionStatus().pipe(take(1)).toPromise();

            if (sessionStatus?.isImpersonating && sessionStatus.session) {
                // For impersonated users, get role from ExtendedUsers using User_ID
                const impersonatedUserId = sessionStatus.session.impersonatedUser.ID;
                const impersonatedEmail = sessionStatus.session.impersonatedUser.Email;

                LogStatus(`Getting role for impersonated user: ${impersonatedEmail}`);
                const userRole = await this.userAuthService.getRoleByUserId(impersonatedUserId, impersonatedEmail);

                if (!userRole) {
                    LogError(`Failed to get role for impersonated user ${impersonatedEmail}`);
                    return null;
                }

                return userRole;
            }

            // For regular users
            let userRole = this.userAuthService.getUserRole();

            if (!userRole) {
                // Check if auth info is available before fetching role
                const personId = this.userAuthService.getPersonId();
                const auraUniqueId = this.userAuthService.getAuraUniqueId();

                if (!personId || !auraUniqueId) {
                    // Auth info not yet available, return null
                    LogStatus('Auth info not yet available for getUserRole');
                    return null;
                }

                userRole = await this.userAuthService.fetchUserRole();
            }

            return userRole;
        } catch (error) {
            LogError('Error getting user role:', undefined, error);
            return null;
        }
    }

    /**
     * Check if the current user has chapter admin privileges
     */
    public async isChapterAdmin(): Promise<boolean> {
        const role = await this.getUserRole();
        return role === 'ChapterAdmin';
    }

    /**
     * Check if the current user has staff privileges
     */
    public async isStaff(): Promise<boolean> {
        const role = await this.getUserRole();
        return role === 'Staff';
    }

    /**
     * Check if the current user has any access (not "No Access")
     */
    public async hasAccess(): Promise<boolean> {
        const role = await this.getUserRole();
        return role !== null && role !== 'No Access';
    }

    /**
     * Check if the current user has a specific role
     */
    public async hasRole(roleName: string): Promise<boolean> {
        const role = await this.getUserRole();
        return role === roleName;
    }
}